import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Bell,
  Calendar,
  ChevronDown,
  Clock,
  FileText,
  LogOut,
  MessageSquare,
  Package,
  Phone,
  Plus,
  Send,
  Settings,
  ShieldCheck,
  Star,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import CommunityBoxes from '../components/CommunityBoxes';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { setCachedSiteSettings } from '../utils/siteSettingsCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const FEATURE_ICONS = ['Calendar', 'TrendingUp', 'Sparkles', 'MessageCircle', 'Video', 'BarChart3'];

function getProgressEntry(progress, day, subject) {
  const entry = progress?.[day]?.[subject];
  if (entry && typeof entry === 'object') {
    return {
      completed: Boolean(entry.completed),
      questions_solved: entry.questions_solved || '',
    };
  }
  return {
    completed: Boolean(entry),
    questions_solved: '',
  };
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('tr-TR');
}

function userStatusLabel(item) {
  if (item.role === 'user' && item.approved === false) return { text: 'Onay Bekliyor', className: 'text-purple-300' };
  if (item.banned) return { text: 'Engelli', className: 'text-red-400' };
  if (!item.package_type) return { text: 'Paket yok', className: 'text-[#A1A1AA]' };
  if (item.package_active) return { text: 'Aktif', className: 'text-green-400' };
  return { text: 'Pasif', className: 'text-yellow-400' };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [packages, setPackages] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [blogPosts, setBlogPosts] = useState([]);
  const [mentorRatings, setMentorRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('all');

  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [progress, setProgress] = useState({});
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [openHistoryItems, setOpenHistoryItems] = useState({});
  const [deletingHistoryKey, setDeletingHistoryKey] = useState('');
  const [libraryResources, setLibraryResources] = useState([]);

  const [userPackageDialog, setUserPackageDialog] = useState(false);
  const [userPackageForm, setUserPackageForm] = useState({ package_type: '', package_start: '', package_end: '' });

  const [notifyDialog, setNotifyDialog] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyType, setNotifyType] = useState({ site: true, whatsapp: false });

  const [phoneDialog, setPhoneDialog] = useState(false);
  const [phoneForm, setPhoneForm] = useState('');

  const [packageDialog, setPackageDialog] = useState(false);
  const [packageForm, setPackageForm] = useState({});
  const [editingPackage, setEditingPackage] = useState(null);

  const [blogDialog, setBlogDialog] = useState(false);
  const [blogForm, setBlogForm] = useState({});
  const [editingBlog, setEditingBlog] = useState(null);

  const ax = useMemo(() => axios.create({ baseURL: API, withCredentials: true }), []);

  const regularUsers = useMemo(
    () =>
      users
        .filter((item) => item.role === 'user')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr')),
    [users]
  );
  const mentors = useMemo(
    () =>
      users
        .filter((item) => item.role === 'mentor')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr')),
    [users]
  );
  const filteredRegularUsers = useMemo(() => {
    switch (userFilter) {
      case 'expired':
        return regularUsers.filter((item) => item.package_type && !item.package_active);
      case 'pending':
        return regularUsers.filter((item) => item.approved === false);
      case 'active':
        return regularUsers.filter((item) => item.package_active);
      case 'passive':
        return regularUsers.filter(
          (item) => item.approved !== false && !item.banned && item.package_type && !item.package_active
        );
      default:
        return regularUsers;
    }
  }, [regularUsers, userFilter]);
  const unreadNotificationCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );
  const mentorStudentCounts = useMemo(() => {
    const counts = {};
    regularUsers.forEach((item) => {
      if (!item.mentor_id) return;
      counts[item.mentor_id] = (counts[item.mentor_id] || 0) + 1;
    });
    return counts;
  }, [regularUsers]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, notificationsRes, packagesRes, settingsRes, blogRes, mentorRatingsRes] = await Promise.all([
        ax.get('/admin/users'),
        ax.get('/admin/notifications'),
        ax.get('/admin/packages'),
        ax.get('/site-settings'),
        ax.get('/admin/blog'),
        ax.get('/admin/mentor-ratings-summary'),
      ]);

      setUsers(usersRes.data);
      setNotifications(notificationsRes.data);
      setPackages(packagesRes.data);
      setSiteSettings(settingsRes.data || {});
      setCachedSiteSettings(settingsRes.data || {});
      setBlogPosts(blogRes.data || []);
      setMentorRatings(mentorRatingsRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Admin verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [ax]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshNotifications = async () => {
    try {
      const { data } = await ax.get('/admin/notifications');
      setNotifications(data);
    } catch (error) {
      console.error('Admin notifications refresh error:', error);
    }
  };

  const handleTabChange = async (value) => {
    setActiveTab(value);
    if (value === 'notifications' && unreadNotificationCount > 0) {
      try {
        await ax.put('/admin/notifications/read-all');
        setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      } catch (error) {
        console.error('Admin bildirim sıfırlama hatası:', error);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const banUser = async (id) => {
    await ax.put(`/admin/users/${id}/ban`);
    toast.success('Kullanıcı engellendi.');
    loadAll();
  };

  const unbanUser = async (id) => {
    await ax.put(`/admin/users/${id}/unban`);
    toast.success('Kullanıcı engeli kaldırıldı.');
    loadAll();
  };

  const setRole = async (id, role) => {
    await ax.put(`/admin/users/${id}/role`, { role });
    toast.success('Rol güncellendi.');
    loadAll();
  };

  const assignMentor = async (userId, mentorId) => {
    await ax.put(`/admin/users/${userId}/assign-mentor`, { mentor_id: mentorId === 'none' ? null : mentorId });
    toast.success('Mentor atandı.');
    loadAll();
  };

  const approveUser = async (id) => {
    await ax.put(`/admin/users/${id}/approve`);
    toast.success('Üyelik onaylandı.');
    loadAll();
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Bu öğrenciyi ve tüm verilerini sistemden silmek istediğine emin misin?')) return;
    await ax.delete(`/admin/users/${id}`);
    toast.success('Öğrenci sistemden silindi.');
    loadAll();
  };

  const openSchedule = async (item) => {
    try {
      const { data } = await ax.get(`/admin/schedule/${item._id}`);
      setSelectedUser(item);
      setSchedule(data.week_data);
      setProgress(data.progress || {});
      setScheduleHistory(Array.isArray(data.schedule_history) ? data.schedule_history : []);
      setOpenHistoryItems({});
      setLibraryResources(Array.isArray(data.library_resources) ? data.library_resources : []);
      setScheduleDialog(true);
    } catch (error) {
      toast.error('Çizelge yüklenemedi.');
    }
  };

  const saveSchedule = async () => {
    await ax.put(`/admin/schedule/${selectedUser._id}`, { week_data: schedule });
    toast.success('Çizelge kaydedildi.');
    setScheduleDialog(false);
  };

  const toggleHistoryItem = (historyKey) => {
    setOpenHistoryItems((prev) => ({
      ...prev,
      [historyKey]: !prev[historyKey],
    }));
  };

  const deleteHistoryItem = async (historyKey) => {
    if (!selectedUser) return;
    if (!window.confirm('Bu gecmis cizelge kaydini silmek istedigine emin misin?')) return;

    try {
      setDeletingHistoryKey(historyKey);
      await ax.delete(`/admin/schedule/${selectedUser._id}/history/${historyKey}`);
      const { data } = await ax.get(`/admin/schedule/${selectedUser._id}`);
      setSchedule(data.week_data);
      setProgress(data.progress || {});
      setScheduleHistory(Array.isArray(data.schedule_history) ? data.schedule_history : []);
      setOpenHistoryItems({});
      setLibraryResources(Array.isArray(data.library_resources) ? data.library_resources : []);
      toast.success('Gecmis cizelge silindi.');
    } catch (error) {
      toast.error('Gecmis cizelge silinemedi.');
    } finally {
      setDeletingHistoryKey('');
    }
  };

  const addSubject = (day) => {
    const subject = window.prompt(`${day} için konu gir:`);
    if (!subject?.trim()) return;
    setSchedule((prev) => ({
      ...prev,
      [day]: { subjects: [...(prev?.[day]?.subjects || []), subject.trim()] },
    }));
  };

  const removeSubject = (day, index) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { subjects: prev?.[day]?.subjects?.filter((_, subjectIndex) => subjectIndex !== index) || [] },
    }));
  };

  const openUserPackage = (item) => {
    setSelectedUser(item);
    setUserPackageForm({
      package_type: item.package_type || '',
      package_start: item.package_start ? item.package_start.substring(0, 10) : '',
      package_end: item.package_end ? item.package_end.substring(0, 10) : '',
    });
    setUserPackageDialog(true);
  };

  const saveUserPackage = async () => {
    await ax.put(`/admin/users/${selectedUser._id}/package`, userPackageForm);
    toast.success('Paket bilgisi güncellendi.');
    setUserPackageDialog(false);
    loadAll();
  };

  const openNotifyDialog = (item) => {
    setSelectedUser(item);
    setNotifyMessage('');
    setNotifyType({ site: true, whatsapp: false });
    setNotifyDialog(true);
  };

  const sendNotification = async () => {
    if (!notifyMessage.trim()) return;

    if (notifyType.site) {
      await ax.post(`/admin/notify-user/${selectedUser._id}`, { message: notifyMessage.trim() });
    }

    if (notifyType.whatsapp) {
      const phone = selectedUser.phone?.replace(/[^0-9]/g, '');
      if (!phone) {
        toast.error('Bu kullanıcının telefon numarası kayıtlı değil.');
        return;
      }
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(notifyMessage.trim())}`, '_blank');
    }

    toast.success('Bildirim işlemi tamamlandı.');
    setNotifyDialog(false);
    refreshNotifications();
  };

  const openPhoneDialog = (item) => {
    setSelectedUser(item);
    setPhoneForm(item.phone || '');
    setPhoneDialog(true);
  };

  const savePhone = async () => {
    await ax.put(`/admin/users/${selectedUser._id}/phone`, { phone: phoneForm });
    toast.success('Telefon güncellendi.');
    setPhoneDialog(false);
    loadAll();
  };

  const openPackageEditor = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm(
      pkg || {
        name: '',
        exam_type: '',
        price: '',
        currency: 'TL',
        period: 'Aylık',
        description: '',
        features: [],
        image_url: '',
        shopier_link: '',
        order: 1,
        popular: false,
      }
    );
    setPackageDialog(true);
  };

  const savePackage = async () => {
    const payload = {
      ...packageForm,
      features: Array.isArray(packageForm.features)
        ? packageForm.features
        : String(packageForm.features || '')
            .split('\n')
            .map((item) => item.trim())
            .filter(Boolean),
    };

    if (editingPackage?._id) {
      await ax.put(`/admin/packages/${editingPackage._id}`, payload);
    } else {
      await ax.post('/admin/packages', payload);
    }
    toast.success('Paket kaydedildi.');
    setPackageDialog(false);
    loadAll();
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Paketi silmek istediğine emin misin?')) return;
    await ax.delete(`/admin/packages/${id}`);
    toast.success('Paket silindi.');
    loadAll();
  };

  const saveSiteSettings = async () => {
    await ax.put('/admin/site-settings', siteSettings);
    setCachedSiteSettings(siteSettings);
    toast.success('Site ayarları güncellendi.');
  };

  const updateSiteFeature = (index, field, value) => {
    setSiteSettings((prev) => {
      const currentFeatures = Array.isArray(prev.features) ? prev.features : [];
      const nextFeatures = currentFeatures.map((feature, featureIndex) =>
        featureIndex === index ? { ...feature, [field]: value } : feature
      );
      return { ...prev, features: nextFeatures };
    });
  };

  const addSiteFeature = () => {
    setSiteSettings((prev) => ({
      ...prev,
      features: [
        ...(Array.isArray(prev.features) ? prev.features : []),
        {
          title: 'Yeni Özellik',
          description: 'Bu kutunun açıklamasını buradan düzenleyin.',
          icon: 'Sparkles',
        },
      ],
    }));
  };

  const removeSiteFeature = (index) => {
    setSiteSettings((prev) => ({
      ...prev,
      features: (Array.isArray(prev.features) ? prev.features : []).filter(
        (_, featureIndex) => featureIndex !== index
      ),
    }));
  };

  const updateSiteArrayItem = (key, index, field, value) => {
    setSiteSettings((prev) => {
      const items = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
      };
    });
  };

  const addCommunityBox = () => {
    setSiteSettings((prev) => ({
      ...prev,
      community_boxes: [
        ...(Array.isArray(prev.community_boxes) ? prev.community_boxes : []),
        { title: 'Yeni Topluluk Kutusu', description: 'Açıklamayı düzenleyin.', whatsapp_url: '' },
      ],
    }));
  };

  const removeCommunityBox = (index) => {
    setSiteSettings((prev) => ({
      ...prev,
      community_boxes: (Array.isArray(prev.community_boxes) ? prev.community_boxes : []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const addAboutStat = () => {
    setSiteSettings((prev) => ({
      ...prev,
      about_stats: [
        ...(Array.isArray(prev.about_stats) ? prev.about_stats : []),
        { value: 'Yeni Değer', label: 'Yeni Başlık' },
      ],
    }));
  };

  const removeAboutStat = (index) => {
    setSiteSettings((prev) => ({
      ...prev,
      about_stats: (Array.isArray(prev.about_stats) ? prev.about_stats : []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const updateSiteOptionList = (key, value) => {
    setSiteSettings((prev) => ({
      ...prev,
      [key]: String(value || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean),
    }));
  };

  const handleAboutImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSiteSettings((prev) => ({ ...prev, about_image_upload: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const handleBlogImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBlogForm((prev) => ({ ...prev, image_url: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const openBlogEditor = (post) => {
    setEditingBlog(post);
    setBlogForm(post || { title: '', content: '', image_url: '', published: true });
    setBlogDialog(true);
  };

  const saveBlog = async () => {
    if (editingBlog?._id) {
      await ax.put(`/admin/blog/${editingBlog._id}`, blogForm);
    } else {
      await ax.post('/admin/blog', blogForm);
    }
    toast.success('Blog kaydedildi.');
    setBlogDialog(false);
    loadAll();
  };

  const deleteBlog = async (id) => {
    if (!window.confirm('Blog yazısını silmek istediğine emin misin?')) return;
    await ax.delete(`/admin/blog/${id}`);
    toast.success('Blog silindi.');
    loadAll();
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#B026FF]">Yükleniyor...</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="bg-black/60 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center pl-1 hover:opacity-90 transition-opacity">
            <span className="text-[1.7rem] font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              KARGA AKADEMİ
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Bell className="w-5 h-5 text-[#E0E0E0]" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {unreadNotificationCount}
                </span>
              )}
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 text-white hover:text-[#B026FF]">
              <LogOut className="w-4 h-4 mr-2" /> Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8" data-testid="admin-dashboard">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 mb-8 flex-wrap h-auto p-1">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Kullanıcılar
            </TabsTrigger>
            <TabsTrigger value="mentors" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <ShieldCheck className="w-4 h-4 mr-2" /> Mentorler
            </TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" /> Paketler
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" /> Site İçeriği
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" /> Blog
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#B026FF] data-[state=active]:text-white">
              <Bell className="w-4 h-4 mr-2" /> Bildirimler {unreadNotificationCount > 0 ? `(${unreadNotificationCount})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kullanıcı Yönetimi ({filteredRegularUsers.length}/{regularUsers.length})
              </h2>
              <div className="w-full lg:w-[280px]">
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                    <SelectValue placeholder="Filtre seç" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="all" className="text-white">Tüm Kullanıcılar</SelectItem>
                    <SelectItem value="pending" className="text-white">Onay Bekleyenler</SelectItem>
                    <SelectItem value="active" className="text-white">Aktif Kullanıcılar</SelectItem>
                    <SelectItem value="passive" className="text-white">Pasif Kullanıcılar</SelectItem>
                    <SelectItem value="expired" className="text-white">Paketi Bitenler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Kullanıcı</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Paket Bilgisi</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Onay ve Durum</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Mentor</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[#E0E0E0]">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegularUsers.map((item) => {
                    const status = userStatusLabel(item);
                    return (
                      <tr key={`clean-${item._id}`} className="border-b border-white/5 hover:bg-white/5 align-top">
                        <td className="px-4 py-4 text-sm">
                          <div className="space-y-2 min-w-0">
                            <div>
                              <p className="font-semibold text-white break-words">{item.name}</p>
                              <p className="text-[#A1A1AA] text-xs break-all">{item.email}</p>
                              <p className="text-[#71717A] text-xs mt-1">{item.class_level || '-'} | {item.field_branch || '-'}</p>
                            </div>
                            <div className="space-y-1 text-xs text-[#A1A1AA]">
                              <p>Şifre: <span className="text-white">{item.visible_password || 'Gizli'}</span></p>
                              <button onClick={() => openPhoneDialog(item)} className="hover:text-white inline-flex items-center gap-1 break-all text-left">
                                <Phone className="w-3 h-3" />
                                {item.phone || 'Telefon ekle'}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="space-y-1 text-[#A1A1AA]">
                            <p className="text-white break-words">{item.package_type || 'Paket yok'}</p>
                            <p className="text-xs">Başlangıç: {formatDate(item.package_start)}</p>
                            <p className="text-xs">Bitiş: {formatDate(item.package_end)}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="space-y-1">
                            <p className={item.approved === false ? 'text-purple-300 font-semibold' : 'text-green-400 font-semibold'}>
                              {item.approved === false ? 'Onay Bekliyor' : 'Onaylı'}
                            </p>
                            <p className={`text-sm ${status.className}`}>{status.text}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <Select value={item.mentor_id || 'none'} onValueChange={(value) => assignMentor(item._id, value)}>
                            <SelectTrigger className="bg-transparent border-white/10 text-white h-9 w-full min-w-[160px]">
                              <SelectValue placeholder="Seç" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              <SelectItem value="none" className="text-white">
                                Yok
                              </SelectItem>
                              {mentors.map((mentor) => (
                                <SelectItem key={`clean-mentor-${item._id}-${mentor._id}`} value={mentor._id} className="text-white">
                                  {mentor.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex flex-wrap justify-end gap-2 max-w-[340px] ml-auto">
                            <Button onClick={() => openSchedule(item)} size="sm" className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                              <Calendar className="w-3 h-3 mr-1" /> Çizelge
                            </Button>
                            {item.approved === false && (
                              <Button onClick={() => approveUser(item._id)} size="sm" variant="outline" className="border-purple-500/50 text-purple-300">
                                <ShieldCheck className="w-3 h-3 mr-1" /> Onayla
                              </Button>
                            )}
                            <Button onClick={() => openUserPackage(item)} size="sm" variant="outline" className="border-white/10 text-white">
                              <Package className="w-3 h-3 mr-1" /> Paket
                            </Button>
                            {item.banned ? (
                              <Button onClick={() => unbanUser(item._id)} size="sm" variant="outline" className="border-green-500/50 text-green-400">
                                <ShieldCheck className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button onClick={() => banUser(item._id)} size="sm" variant="outline" className="border-red-500/50 text-red-400">
                                <Ban className="w-3 h-3" />
                              </Button>
                            )}
                            <Button onClick={() => setRole(item._id, 'mentor')} size="sm" variant="outline" className="border-blue-500/50 text-blue-400">
                              <UserPlus className="w-3 h-3" />
                            </Button>
                            <Button onClick={() => openNotifyDialog(item)} size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400">
                              <Bell className="w-3 h-3" />
                            </Button>
                            <Button onClick={() => deleteUser(item._id)} size="sm" variant="outline" className="border-red-500/50 text-red-300">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="hidden space-y-4">
              {filteredRegularUsers.map((item) => {
                const status = userStatusLabel(item);
                return (
                  <div key={`mobile-${item._id}`} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-bold text-white text-base break-words">{item.name}</h3>
                        <p className="text-xs text-[#A1A1AA] break-all">{item.email}</p>
                      </div>
                      <span className={`text-sm font-semibold whitespace-nowrap ${status.className}`}>{status.text}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Şifre</p>
                        <p className="text-white break-words">{item.visible_password || 'Gizli'}</p>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Onay</p>
                        <p className={item.approved === false ? 'text-purple-300' : 'text-green-400'}>
                          {item.approved === false ? 'Bekliyor' : 'Onaylı'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Telefon</p>
                        <button onClick={() => openPhoneDialog(item)} className="text-left text-[#A1A1AA] hover:text-white break-all">
                          {item.phone || 'Ekle'}
                        </button>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Paket</p>
                        <p className="text-[#A1A1AA] break-words">{item.package_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Başlangıç</p>
                        <p className="text-[#A1A1AA]">{formatDate(item.package_start)}</p>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-xs mb-1">Bitiş</p>
                        <p className="text-[#A1A1AA]">{formatDate(item.package_end)}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-[#71717A] text-xs mb-2">Mentor</p>
                      <Select value={item.mentor_id || 'none'} onValueChange={(value) => assignMentor(item._id, value)}>
                        <SelectTrigger className="bg-transparent border-white/10 text-white h-10 w-full">
                          <SelectValue placeholder="Seç" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="none" className="text-white">
                            Yok
                          </SelectItem>
                          {mentors.map((mentor) => (
                            <SelectItem key={mentor._id} value={mentor._id} className="text-white">
                              {mentor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={() => openSchedule(item)} size="sm" className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                        <Calendar className="w-3 h-3 mr-1" /> Çizelge
                      </Button>
                      <Button onClick={() => openUserPackage(item)} size="sm" variant="outline" className="border-white/10 text-white">
                        <Package className="w-3 h-3 mr-1" /> Paket
                      </Button>
                      {item.approved === false && (
                        <Button onClick={() => approveUser(item._id)} size="sm" variant="outline" className="border-purple-500/50 text-purple-300">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Onayla
                        </Button>
                      )}
                      {item.banned ? (
                        <Button onClick={() => unbanUser(item._id)} size="sm" variant="outline" className="border-green-500/50 text-green-400">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Aç
                        </Button>
                      ) : (
                        <Button onClick={() => banUser(item._id)} size="sm" variant="outline" className="border-red-500/50 text-red-400">
                          <Ban className="w-3 h-3 mr-1" /> Kapat
                        </Button>
                      )}
                      <Button onClick={() => setRole(item._id, 'mentor')} size="sm" variant="outline" className="border-blue-500/50 text-blue-400">
                        <UserPlus className="w-3 h-3 mr-1" /> Mentor
                      </Button>
                      <Button onClick={() => openNotifyDialog(item)} size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400">
                        <Bell className="w-3 h-3 mr-1" /> Bildir
                      </Button>
                      <Button onClick={() => deleteUser(item._id)} size="sm" variant="outline" className="col-span-2 border-red-500/50 text-red-300">
                        <Trash2 className="w-3 h-3 mr-1" /> Öğrenciyi Sil
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full table-fixed">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[16%] sm:w-[14%]">Ad</th>
                    <th className="hidden xl:table-cell px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[16%]">E-posta</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Şifre</th>
                    <th className="hidden md:table-cell px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[12%]">Telefon</th>
                    <th className="hidden lg:table-cell px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[10%]">Paket</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Başlangıç</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Bitiş</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[12%]">Onay</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[14%] sm:w-[12%]">Mentor</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-[#E0E0E0] w-[12%]">Durum</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[#E0E0E0]">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegularUsers.map((item) => {
                    const status = userStatusLabel(item);
                    return (
                      <tr key={item._id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 text-sm break-words">{item.name}</td>
                        <td className="hidden xl:table-cell px-4 py-3 text-sm text-[#A1A1AA] break-all">{item.email}</td>
                        <td className="px-4 py-3 text-sm text-[#E0E0E0]">{item.visible_password || 'Gizli'}</td>
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-[#A1A1AA]">
                          <button onClick={() => openPhoneDialog(item)} className="hover:text-white inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {item.phone || 'Ekle'}
                          </button>
                        </td>
                        <td className="hidden lg:table-cell px-4 py-3 text-sm text-[#A1A1AA]">{item.package_type || '-'}</td>
                        <td className="hidden 2xl:table-cell px-4 py-3 text-sm text-[#A1A1AA]">{formatDate(item.package_start)}</td>
                        <td className="hidden xl:table-cell px-4 py-3 text-sm text-[#A1A1AA]">{formatDate(item.package_end)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={item.approved === false ? 'text-purple-300' : 'text-green-400'}>
                            {item.approved === false ? 'Bekliyor' : 'Onaylı'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#A1A1AA]">
                          <Select value={item.mentor_id || 'none'} onValueChange={(value) => assignMentor(item._id, value)}>
                            <SelectTrigger className="bg-transparent border-white/10 text-white h-8 w-full min-w-0 max-w-[140px]">
                              <SelectValue placeholder="Seç" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              <SelectItem value="none" className="text-white">
                                Yok
                              </SelectItem>
                              {mentors.map((mentor) => (
                                <SelectItem key={mentor._id} value={mentor._id} className="text-white">
                                  {mentor.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className={`px-4 py-3 text-sm break-words ${status.className}`}>{status.text}</td>
                        <td className="px-2 py-3 text-left sm:text-right">
                          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                          <Button onClick={() => openSchedule(item)} size="sm" className="bg-[#B026FF] hover:bg-[#C966FF] text-white w-full sm:w-auto px-2">
                            <Calendar className="w-3 h-3 mr-1" /> Çizelge
                          </Button>
                          {item.approved === false && (
                            <Button onClick={() => approveUser(item._id)} size="sm" variant="outline" className="border-purple-500/50 text-purple-300 w-full sm:w-auto px-2">
                              <ShieldCheck className="w-3 h-3 mr-1" /> Onayla
                            </Button>
                          )}
                          <Button onClick={() => openUserPackage(item)} size="sm" variant="outline" className="border-white/10 text-white w-full sm:w-auto px-2">
                            <Package className="w-3 h-3 mr-1" /> Paket
                          </Button>
                          {item.banned ? (
                            <Button onClick={() => unbanUser(item._id)} size="sm" variant="outline" className="border-green-500/50 text-green-400 w-full sm:w-auto px-2">
                              <ShieldCheck className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button onClick={() => banUser(item._id)} size="sm" variant="outline" className="border-red-500/50 text-red-400 w-full sm:w-auto px-2">
                              <Ban className="w-3 h-3" />
                            </Button>
                          )}
                          <Button onClick={() => setRole(item._id, 'mentor')} size="sm" variant="outline" className="border-blue-500/50 text-blue-400 w-full sm:w-auto px-2">
                            <UserPlus className="w-3 h-3" />
                          </Button>
                          <Button onClick={() => openNotifyDialog(item)} size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400 w-full sm:w-auto px-2">
                            <Bell className="w-3 h-3" />
                          </Button>
                          <Button onClick={() => deleteUser(item._id)} size="sm" variant="outline" className="border-red-500/50 text-red-300 w-full sm:w-auto px-2">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-8">
              <CommunityBoxes boxes={siteSettings.community_boxes} title="Topluluk" />
            </div>
          </TabsContent>

          <TabsContent value="mentors">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {mentorRatings.map((item) => (
                <div key={item.mentor_id} className="bg-[#0A0A0A] border border-yellow-500/20 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-[#A1A1AA]">{item.mentor_name}</p>
                      <h3 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {item.average}/5
                      </h3>
                    </div>
                    <Star className="w-8 h-8 text-yellow-400" />
                  </div>
                  <p className="text-sm text-[#71717A]">{item.total} değerlendirme</p>
                  {item.latest_comment && <p className="text-xs text-[#A1A1AA] mt-2 line-clamp-2">Son yorum: {item.latest_comment}</p>}
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mentor Puanları ve Yönetimi ({mentors.length})
            </h2>

            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Ad</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">E-posta</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Şifre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Öğrenci</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Ortalama</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Puan Sayısı</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#E0E0E0]">Durum</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-[#E0E0E0]">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {mentors.map((mentor) => {
                    const ratingInfo = mentorRatings.find((item) => item.mentor_id === mentor._id) || {
                      average: 0,
                      total: 0,
                    };
                    return (
                      <tr key={mentor._id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 text-sm">{mentor.name}</td>
                        <td className="px-4 py-3 text-sm text-[#A1A1AA]">{mentor.email}</td>
                        <td className="px-4 py-3 text-sm text-[#E0E0E0]">{mentor.visible_password || 'Gizli'}</td>
                        <td className="px-4 py-3 text-sm text-[#A1A1AA]">{mentorStudentCounts[mentor._id] || 0}</td>
                        <td className="px-4 py-3 text-sm text-yellow-400">{ratingInfo.average}/5</td>
                        <td className="px-4 py-3 text-sm text-[#A1A1AA]">{ratingInfo.total}</td>
                        <td className={`px-4 py-3 text-sm ${mentor.banned ? 'text-red-400' : 'text-green-400'}`}>
                          {mentor.banned ? 'Engelli' : 'Aktif'}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2">
                          {mentor.banned ? (
                            <Button onClick={() => unbanUser(mentor._id)} size="sm" variant="outline" className="border-green-500/50 text-green-400">
                              Engeli Kaldır
                            </Button>
                          ) : (
                            <Button onClick={() => banUser(mentor._id)} size="sm" variant="outline" className="border-red-500/50 text-red-400">
                              Engelle
                            </Button>
                          )}
                          <Button onClick={() => setRole(mentor._id, 'user')} size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400">
                            Kullanıcı Yap
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="packages">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Paket Yönetimi
              </h2>
              <Button onClick={() => openPackageEditor(null)} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                <Plus className="w-4 h-4 mr-2" /> Yeni Paket
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div key={pkg._id} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {pkg.name}
                  </h3>
                  <p className="text-[#B026FF] text-sm mb-2">
                    {pkg.exam_type} - {pkg.price} {pkg.currency}/{pkg.period}
                  </p>
                  <p className="text-[#A1A1AA] text-sm mb-4">{pkg.description}</p>
                  <div className="space-y-1 text-sm text-[#E0E0E0] mb-4">
                    {(pkg.features || []).map((feature, index) => (
                      <p key={`${pkg._id}-${index}`}>• {feature}</p>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openPackageEditor(pkg)} variant="outline" className="border-white/10 text-white">
                      Düzenle
                    </Button>
                    <Button onClick={() => deletePackage(pkg._id)} variant="outline" className="border-red-500/50 text-red-400">
                      Sil
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="content">
            <div className="max-w-4xl bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Site İçeriği
              </h2>

              <div>
                <Label className="text-white mb-2 block">Ana Başlık</Label>
                <Input
                  value={siteSettings.hero_title || ''}
                  onChange={(event) => setSiteSettings((prev) => ({ ...prev, hero_title: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Alt Başlık</Label>
                <Textarea
                  value={siteSettings.hero_subtitle || ''}
                  onChange={(event) => setSiteSettings((prev) => ({ ...prev, hero_subtitle: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Hakkımızda</Label>
                <Textarea
                  value={siteSettings.about_text || ''}
                  onChange={(event) => setSiteSettings((prev) => ({ ...prev, about_text: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white min-h-[180px]"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Hakkımızda Görsel URL</Label>
                  <Input
                    value={siteSettings.about_image_url || ''}
                    onChange={(event) => setSiteSettings((prev) => ({ ...prev, about_image_url: event.target.value }))}
                    className="bg-black/40 border-white/10 text-white"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Dosya Yükle</Label>
                  <Input type="file" accept="image/*" onChange={handleAboutImageUpload} className="bg-black/40 border-white/10 text-white file:text-white" />
                </div>
              </div>
              <div className="space-y-4 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-white mb-1 block">Hakkımızda Sayı Kutuları</Label>
                    <p className="text-sm text-[#A1A1AA]">Bu alandaki değer kutularını admin panelden düzenleyebilir, yeni kutular ekleyebilirsin.</p>
                  </div>
                  <Button type="button" onClick={addAboutStat} variant="outline" className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10">
                    <Plus className="w-4 h-4 mr-2" /> Yeni Kutu
                  </Button>
                </div>

                {(Array.isArray(siteSettings.about_stats) ? siteSettings.about_stats : []).length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm text-[#71717A]">
                    Henüz sayı kutusu yok. “Yeni Kutu” ile ekleyebilirsin.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(Array.isArray(siteSettings.about_stats) ? siteSettings.about_stats : []).map((item, index) => (
                      <div key={`about-stat-${index}`} className="rounded-xl border border-white/10 bg-[#050505] p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-white">Sayı Kutusu {index + 1}</h3>
                          <Button type="button" onClick={() => removeAboutStat(index)} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                            <X className="w-4 h-4 mr-2" /> Sil
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-white mb-2 block">Değer</Label>
                            <Input
                              value={item?.value || ''}
                              onChange={(event) => updateSiteArrayItem('about_stats', index, 'value', event.target.value)}
                              className="bg-black/40 border-white/10 text-white"
                              placeholder="15.000+"
                            />
                          </div>
                          <div>
                            <Label className="text-white mb-2 block">Başlık</Label>
                            <Input
                              value={item?.label || ''}
                              onChange={(event) => updateSiteArrayItem('about_stats', index, 'label', event.target.value)}
                              className="bg-black/40 border-white/10 text-white"
                              placeholder="Öğrenci"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Ana Sayfa 1. Buton</Label>
                  <Input
                    value={siteSettings.hero_primary_button_text || ''}
                    onChange={(event) => setSiteSettings((prev) => ({ ...prev, hero_primary_button_text: event.target.value }))}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Ana Sayfa 2. Buton</Label>
                  <Input
                    value={siteSettings.hero_secondary_button_text || ''}
                    onChange={(event) => setSiteSettings((prev) => ({ ...prev, hero_secondary_button_text: event.target.value }))}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Kayıt Sınıf Seçenekleri</Label>
                  <Textarea
                    value={(Array.isArray(siteSettings.register_class_options) ? siteSettings.register_class_options : []).join('\n')}
                    onChange={(event) => updateSiteOptionList('register_class_options', event.target.value)}
                    className="bg-black/40 border-white/10 text-white min-h-[140px]"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">Kayıt Alan Seçenekleri</Label>
                  <Textarea
                    value={(Array.isArray(siteSettings.register_field_options) ? siteSettings.register_field_options : []).join('\n')}
                    onChange={(event) => updateSiteOptionList('register_field_options', event.target.value)}
                    className="bg-black/40 border-white/10 text-white min-h-[140px]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Kayıt Buton Yazısı</Label>
                <Input
                  value={siteSettings.register_submit_text || ''}
                  onChange={(event) => setSiteSettings((prev) => ({ ...prev, register_submit_text: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">İletişim E-postası</Label>
                  <Input
                    value={siteSettings.contact_email || ''}
                    onChange={(event) => setSiteSettings((prev) => ({ ...prev, contact_email: event.target.value }))}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">İletişim Telefonu</Label>
                  <Input
                    value={siteSettings.contact_phone || ''}
                    onChange={(event) => setSiteSettings((prev) => ({ ...prev, contact_phone: event.target.value }))}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Footer Metni</Label>
                <Input
                  value={siteSettings.footer_text || ''}
                  onChange={(event) => setSiteSettings((prev) => ({ ...prev, footer_text: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div className="space-y-4 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-white mb-1 block">Ana Sayfa Özellik Kutuları</Label>
                    <p className="text-sm text-[#A1A1AA]">
                      “Neden Karga Akademi?” alanındaki kutuları buradan düzenleyebilir, yenisini ekleyebilirsin.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={addSiteFeature}
                    variant="outline"
                    className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Yeni Kutu
                  </Button>
                </div>

                {(Array.isArray(siteSettings.features) ? siteSettings.features : []).length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/10 p-4 text-sm text-[#71717A]">
                    Henüz özellik kutusu yok. “Yeni Kutu” ile ekleyebilirsin.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(Array.isArray(siteSettings.features) ? siteSettings.features : []).map((feature, index) => (
                      <div key={`site-feature-${index}`} className="rounded-xl border border-white/10 bg-[#050505] p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-white">Kutu {index + 1}</h3>
                          <Button
                            type="button"
                            onClick={() => removeSiteFeature(index)}
                            variant="outline"
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4 mr-2" /> Sil
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-white mb-2 block">Başlık</Label>
                            <Input
                              value={feature?.title || ''}
                              onChange={(event) => updateSiteFeature(index, 'title', event.target.value)}
                              className="bg-black/40 border-white/10 text-white"
                            />
                          </div>
                          <div>
                            <Label className="text-white mb-2 block">İkon</Label>
                            <Select
                              value={feature?.icon || 'Sparkles'}
                              onValueChange={(value) => updateSiteFeature(index, 'icon', value)}
                            >
                              <SelectTrigger className="bg-black/40 border-white/10 text-white">
                                <SelectValue placeholder="İkon seç" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A0A] border-white/10">
                                {FEATURE_ICONS.map((icon) => (
                                  <SelectItem key={icon} value={icon} className="text-white">
                                    {icon}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label className="text-white mb-2 block">Açıklama</Label>
                          <Textarea
                            value={feature?.description || ''}
                            onChange={(event) => updateSiteFeature(index, 'description', event.target.value)}
                            className="bg-black/40 border-white/10 text-white min-h-[96px]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-4 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-white mb-1 block">Topluluk Kutuları</Label>
                    <p className="text-sm text-[#A1A1AA]">Kullanıcı, mentor, admin ve ana sayfada görünen WhatsApp kutularını buradan düzenle.</p>
                  </div>
                  <Button type="button" onClick={addCommunityBox} variant="outline" className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10">
                    <Plus className="w-4 h-4 mr-2" /> Yeni Kutu
                  </Button>
                </div>

                {(Array.isArray(siteSettings.community_boxes) ? siteSettings.community_boxes : []).map((item, index) => (
                  <div key={`community-box-${index}`} className="rounded-xl border border-white/10 bg-[#050505] p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white">Topluluk Kutusu {index + 1}</h3>
                      <Button type="button" onClick={() => removeCommunityBox(index)} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                        <X className="w-4 h-4 mr-2" /> Sil
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white mb-2 block">Başlık</Label>
                        <Input
                          value={item?.title || ''}
                          onChange={(event) => updateSiteArrayItem('community_boxes', index, 'title', event.target.value)}
                          className="bg-black/40 border-white/10 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">WhatsApp Bağlantısı</Label>
                        <Input
                          value={item?.whatsapp_url || ''}
                          onChange={(event) => updateSiteArrayItem('community_boxes', index, 'whatsapp_url', event.target.value)}
                          className="bg-black/40 border-white/10 text-white"
                          placeholder="https://wa.me/90..."
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">Açıklama</Label>
                      <Textarea
                        value={item?.description || ''}
                        onChange={(event) => updateSiteArrayItem('community_boxes', index, 'description', event.target.value)}
                        className="bg-black/40 border-white/10 text-white min-h-[96px]"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveSiteSettings} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                Kaydet
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="blog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Blog Yönetimi
              </h2>
              <Button onClick={() => openBlogEditor(null)} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
                <Plus className="w-4 h-4 mr-2" /> Yeni Yazı
              </Button>
            </div>

            <div className="space-y-4">
              {blogPosts.map((post) => (
                <div key={post._id} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#71717A] mb-2">{formatDate(post.created_at)}</p>
                    <p className="text-sm text-[#A1A1AA] line-clamp-3">{post.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openBlogEditor(post)} variant="outline" className="border-white/10 text-white">
                      Düzenle
                    </Button>
                    <Button onClick={() => deleteBlog(post._id)} variant="outline" className="border-red-500/50 text-red-400">
                      Sil
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-8">
              {notifications.length === 0 && <p className="text-[#71717A] text-center py-8">Bildirim yok.</p>}

              {notifications.filter((item) => item.type === 'package_expired').length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <h3 className="text-lg font-bold text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Paketi Bitenler
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {notifications
                      .filter((item) => item.type === 'package_expired')
                      .map((item) => (
                        <div
                          key={item.notification_key}
                          className={`rounded-lg border p-4 flex items-center gap-4 ${
                            item.read ? 'border-white/10' : 'border-red-500/30 bg-red-500/5'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-white">{item.user_name}</h4>
                            <p className="text-sm text-[#A1A1AA]">{item.user_email}</p>
                            <p className="text-xs text-red-300 mt-1">
                              Paket: {item.package_type || '-'} | Bitiş: {formatDate(item.package_end)}
                            </p>
                          </div>
                          <Button onClick={() => openNotifyDialog({ _id: item.user_id, name: item.user_name, phone: item.user_phone })} size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                            <Send className="w-3 h-3 mr-1" /> Bildirim
                          </Button>
                        </div>
                      ))}
                  </div>
                </section>
              )}

              {notifications.filter((item) => item.type === 'package_expiring_soon').length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <h3 className="text-lg font-bold text-yellow-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Paketi Yakında Bitecekler
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {notifications
                      .filter((item) => item.type === 'package_expiring_soon')
                      .map((item) => (
                        <div
                          key={item.notification_key}
                          className={`rounded-lg border p-4 flex items-center gap-4 ${
                            item.read ? 'border-white/10' : 'border-yellow-500/30 bg-yellow-500/5'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-white">{item.user_name}</h4>
                            <p className="text-sm text-[#A1A1AA]">{item.user_email}</p>
                            <p className="text-xs text-yellow-300 mt-1">
                              Paket: {item.package_type || '-'} | Bitiş: {formatDate(item.package_end)}
                            </p>
                          </div>
                          <Button onClick={() => openNotifyDialog({ _id: item.user_id, name: item.user_name, phone: item.user_phone })} size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                            <Send className="w-3 h-3 mr-1" /> Bildirim
                          </Button>
                        </div>
                      ))}
                  </div>
                </section>
              )}

              {notifications.filter((item) => item.type === 'mentor_rating').length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#B026FF]" />
                    <h3 className="text-lg font-bold text-[#B026FF]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Mentor Puanlamaları
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {notifications
                      .filter((item) => item.type === 'mentor_rating')
                      .map((item) => (
                        <div
                          key={item.notification_key}
                          className={`rounded-lg border p-4 flex items-center gap-4 ${
                            item.read ? 'border-white/10' : 'border-[#B026FF]/30 bg-[#B026FF]/5'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-[#B026FF]/10 flex items-center justify-center flex-shrink-0">
                            <Star className="w-5 h-5 text-[#B026FF]" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-white">{item.user_name} değerlendirme bıraktı</h4>
                            <p className="text-sm text-[#A1A1AA]">
                              Mentor: {item.mentor_name} | Puan: {item.rating}/5
                            </p>
                            {item.comment && <p className="text-xs text-[#CFCFCF] mt-1">"{item.comment}"</p>}
                          </div>
                        </div>
                      ))}
                  </div>
                </section>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedUser?.name} - Çizelge
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Kullanıcının tamamladığı görevler burada yeşil ve üzeri çizili görünür.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {DAYS.map((day) => (
              <div key={day} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {day}
                  </h4>
                  <Button onClick={() => addSubject(day)} size="sm" variant="outline" className="border-[#B026FF]/50 text-[#B026FF]">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {(schedule?.[day]?.subjects || []).map((subject, index) => {
                    const progressEntry = getProgressEntry(progress, day, subject);
                    const completed = progressEntry.completed;
                    return (
                      <div
                        key={`${day}-${index}-${subject}`}
                        className={`flex items-center justify-between rounded px-3 py-2 border ${
                          completed
                            ? 'bg-green-500/10 border-green-500/30 text-green-100'
                            : 'bg-black/40 border-white/10'
                        }`}
                      >
                        <div>
                          <span className={`text-sm ${completed ? 'line-through' : 'text-[#E0E0E0]'}`}>{subject}</span>
                          {progressEntry.questions_solved && (
                            <p className="text-[11px] text-[#A1A1AA] mt-1">Günlük çözülen soru: {progressEntry.questions_solved}</p>
                          )}
                        </div>
                        <button onClick={() => removeSubject(day, index)} className="text-red-400 hover:text-red-300">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  {!(schedule?.[day]?.subjects?.length) && <p className="text-[#71717A] text-sm italic">Konu yok.</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#B026FF]" />
              <h4 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Gecmis Cizelgeyi Goruntule
              </h4>
            </div>
            {scheduleHistory.length === 0 ? (
              <p className="text-sm text-[#71717A] italic">Bu kullanici icin henuz gecmis cizelge bulunmuyor.</p>
            ) : (
              <div className="space-y-4">
                {scheduleHistory.map((historyItem, historyIndex) => {
                  const historyKey = historyItem._id || `${historyItem.schedule_version}-${historyIndex}`;
                  const isOpen = Boolean(openHistoryItems[historyKey]);
                  return (
                    <div key={historyKey} className="rounded-lg border border-white/10 bg-black/30">
                      <div className="flex items-center gap-3 px-4 py-4">
                        <button
                          type="button"
                          onClick={() => toggleHistoryItem(historyKey)}
                          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
                        >
                          <div>
                            <p className="text-sm font-semibold text-white">
                              Gecmis Cizelge #{historyItem.schedule_version || historyIndex + 1}
                            </p>
                            <p className="text-xs text-[#A1A1AA]">
                              Olusturma: {formatDate(historyItem.created_at)} | Gecmise dusme: {formatDate(historyItem.archived_at)}
                            </p>
                          </div>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-[#A1A1AA] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => deleteHistoryItem(historyKey)}
                          disabled={deletingHistoryKey === historyKey}
                          className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {deletingHistoryKey === historyKey ? 'Siliniyor...' : 'Sil'}
                        </Button>
                      </div>
                      {isOpen && (
                        <div className="border-t border-white/10 px-4 pb-4 pt-4">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {DAYS.map((day) => {
                              const dayData = historyItem.week_data?.[day] || { subjects: [] };
                              return (
                                <div key={`${historyKey}-${day}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                                  <h5 className="mb-2 font-semibold text-white">{day}</h5>
                                  {dayData.subjects?.length > 0 ? (
                                    <div className="space-y-2">
                                      {dayData.subjects.map((subject, subjectIndex) => (
                                        <div key={`${historyKey}-${day}-${subjectIndex}`} className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-[#D4D4D8]">
                                          {subject}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm italic text-[#71717A]">Konu yok.</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <h4 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kullanıcı Kütüphanesi / Kaynakları
            </h4>
            {libraryResources.length === 0 ? (
              <p className="text-sm text-[#71717A] italic">Kullanıcı henüz kaynak eklemedi.</p>
            ) : (
              <div className="space-y-3">
                {libraryResources.map((item) => (
                  <div key={item._id} className="rounded-lg border border-white/10 bg-black/30 p-3">
                    <p className="text-sm font-semibold text-white">{item.title || 'Adsız Kaynak'}</p>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block break-all text-xs text-[#60A5FA] hover:text-[#93C5FD]"
                      >
                        {item.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setScheduleDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button onClick={saveSchedule} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userPackageDialog} onOpenChange={setUserPackageDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedUser?.name} - Paket Bilgisi
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Paket türü ve tarihlerini düzenleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Paket Türü</Label>
              <Input
                value={userPackageForm.package_type || ''}
                onChange={(event) => setUserPackageForm((prev) => ({ ...prev, package_type: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={userPackageForm.package_start || ''}
                onChange={(event) => setUserPackageForm((prev) => ({ ...prev, package_start: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={userPackageForm.package_end || ''}
                onChange={(event) => setUserPackageForm((prev) => ({ ...prev, package_end: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setUserPackageDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button onClick={saveUserPackage} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notifyDialog} onOpenChange={setNotifyDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedUser?.name} için Bildirim
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Site bildirimi ve/veya WhatsApp bildirimi gönder.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            <Textarea
              value={notifyMessage}
              onChange={(event) => setNotifyMessage(event.target.value)}
              placeholder="Bildirim mesajını yaz..."
              className="bg-black/40 border-white/10 text-white min-h-[100px]"
            />

            <div className="space-y-3">
              <Label className="text-white text-sm font-semibold block">Gönderim Yöntemi</Label>
              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                  notifyType.site ? 'border-[#B026FF] bg-[#B026FF]/10' : 'border-white/10 bg-white/5'
                }`}
              >
                <input
                  type="checkbox"
                  checked={notifyType.site}
                  onChange={(event) => setNotifyType((prev) => ({ ...prev, site: event.target.checked }))}
                  className="accent-[#B026FF] w-4 h-4"
                />
                <div>
                  <p className="text-sm font-semibold text-white">Site Bildirimi</p>
                  <p className="text-xs text-[#71717A]">Kullanıcı panelinde görünür.</p>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                  notifyType.whatsapp ? 'border-green-500 bg-green-500/10' : 'border-white/10 bg-white/5'
                }`}
              >
                <input
                  type="checkbox"
                  checked={notifyType.whatsapp}
                  onChange={(event) => setNotifyType((prev) => ({ ...prev, whatsapp: event.target.checked }))}
                  className="accent-green-500 w-4 h-4"
                />
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-green-400" />
                  <div>
                    <p className="text-sm font-semibold text-white">WhatsApp Bildirimi</p>
                    <p className="text-xs text-[#71717A]">{selectedUser?.phone || 'Telefon numarası kayıtlı değil'}</p>
                  </div>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNotifyDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button
              onClick={sendNotification}
              disabled={!notifyMessage.trim() || (!notifyType.site && !notifyType.whatsapp)}
              className="bg-[#B026FF] hover:bg-[#C966FF] text-white"
            >
              <Send className="w-4 h-4 mr-2" /> Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={phoneDialog} onOpenChange={setPhoneDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedUser?.name} - Telefon
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              WhatsApp bildirimi için telefon numarasını güncelle.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-white mb-2 block">Telefon Numarası</Label>
            <Input
              value={phoneForm}
              onChange={(event) => setPhoneForm(event.target.value)}
              placeholder="+905xxxxxxxxx"
              className="bg-black/40 border-white/10 text-white"
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setPhoneDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button onClick={savePhone} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={packageDialog} onOpenChange={setPackageDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingPackage ? 'Paket Düzenle' : 'Yeni Paket'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Paket detaylarını girin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Paket Adı</Label>
                <Input
                  value={packageForm.name || ''}
                  onChange={(event) => setPackageForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Sınav Türü</Label>
                <Input
                  value={packageForm.exam_type || ''}
                  onChange={(event) => setPackageForm((prev) => ({ ...prev, exam_type: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-white mb-2 block">Fiyat</Label>
                <Input
                  value={packageForm.price || ''}
                  onChange={(event) => setPackageForm((prev) => ({ ...prev, price: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Para Birimi</Label>
                <Input
                  value={packageForm.currency || 'TL'}
                  onChange={(event) => setPackageForm((prev) => ({ ...prev, currency: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">Periyot</Label>
                <Input
                  value={packageForm.period || 'Aylık'}
                  onChange={(event) => setPackageForm((prev) => ({ ...prev, period: event.target.value }))}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Açıklama</Label>
              <Textarea
                value={packageForm.description || ''}
                onChange={(event) => setPackageForm((prev) => ({ ...prev, description: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Özellikler (her satır bir özellik)</Label>
              <Textarea
                value={Array.isArray(packageForm.features) ? packageForm.features.join('\n') : packageForm.features || ''}
                onChange={(event) => setPackageForm((prev) => ({ ...prev, features: event.target.value }))}
                className="bg-black/40 border-white/10 text-white min-h-[110px]"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Görsel URL</Label>
              <Input
                value={packageForm.image_url || ''}
                onChange={(event) => setPackageForm((prev) => ({ ...prev, image_url: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Ödeme Linki</Label>
              <Input
                value={packageForm.shopier_link || ''}
                onChange={(event) => setPackageForm((prev) => ({ ...prev, shopier_link: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPackageDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button onClick={savePackage} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={blogDialog} onOpenChange={setBlogDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingBlog ? 'Blog Düzenle' : 'Yeni Blog Yazısı'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Blog içeriğini düzenleyin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Başlık</Label>
              <Input
                value={blogForm.title || ''}
                onChange={(event) => setBlogForm((prev) => ({ ...prev, title: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">İçerik</Label>
              <Textarea
                value={blogForm.content || ''}
                onChange={(event) => setBlogForm((prev) => ({ ...prev, content: event.target.value }))}
                className="bg-black/40 border-white/10 text-white min-h-[220px]"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Görsel URL</Label>
              <Input
                value={blogForm.image_url || ''}
                onChange={(event) => setBlogForm((prev) => ({ ...prev, image_url: event.target.value }))}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Görsel Yükle</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleBlogImageUpload}
                className="bg-black/40 border-white/10 text-white file:text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setBlogDialog(false)} variant="outline" className="border-white/10 text-white">
              İptal
            </Button>
            <Button onClick={saveBlog} className="bg-[#B026FF] hover:bg-[#C966FF] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
